import React, { useState, useRef } from 'react';
import axios from 'axios';
import { PositionData } from '@/pages/hettwer/types/PositionData';
import { KeenIcon } from '@/components';
import { enqueueSnackbar } from 'notistack';

interface PositionsBlockProps {
  positions: PositionData[];
  onChange: (positions: PositionData[]) => void;
  isDraft: boolean;
}

interface SourcePreview {
  position: { title?: string | null; description?: string | null };
  offer: { offer_number?: string | null; document_type?: string | null };
  files: { original_name: string; download_url: string }[];
}

/* ================================================== Calculation helpers ================================================== */
const round2 = (v: number) => Math.round(v * 100) / 100;
const normalizeQuantity = (q?: number | null) => (q == null || q <= 0 ? 1 : q);

const recalcPosition = (
  pos: PositionData & { _lastChanged?: 'unit_price' | 'total_price' | 'quantity' }
): PositionData => {
  const quantity = normalizeQuantity(pos.measurement?.quantity);
  const unit = pos.pricing?.unit_price;
  const total = pos.pricing?.total_price;
  const next: any = { ...pos, measurement: { ...(pos.measurement ?? {}), quantity }, pricing: { ...(pos.pricing ?? {}) } };

  switch (pos._lastChanged) {
    case 'unit_price':
      if (unit != null) next.pricing.total_price = round2(unit * quantity);
      break;
    case 'total_price':
      if (total != null) next.pricing.unit_price = round2(total / quantity);
      break;
    case 'quantity':
      if (unit != null) {
        next.pricing.total_price = round2(unit * quantity);
      } else if (total != null) {
        next.pricing.unit_price = round2(total / quantity);
      }
      break;
  }
  delete next._lastChanged;
  return next;
};

const needsQuantityWarning = (pos: PositionData) =>
  (pos.pricing?.unit_price != null || pos.pricing?.total_price != null) && pos.measurement?.quantity == null;

/* ================================================== Component ================================================== */
export const PositionsBlock: React.FC<PositionsBlockProps> = ({ positions, onChange, isDraft }) => {
  const API_BASE = import.meta.env.VITE_APP_API_URL;

  // State for Previews
  const [sourcePreviews, setSourcePreviews] = useState<Record<number, SourcePreview | null>>({});
  const [loadingSources, setLoadingSources] = useState<Record<number, boolean>>({});

  // State for Dictation
  const [recordingIndex, setRecordingIndex] = useState<number | null>(null);
  const [isProcessingDictation, setIsProcessingDictation] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [voiceDetected, setVoiceDetected] = useState(false);

  // Refs for Audio
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);

  /* ================================================== Dictation Logic ================================================== */
  const getBestSupportedMimeType = (): string => {
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/wav'];
    for (const type of candidates) {
      if (MediaRecorder.isTypeSupported(type)) return type;
    }
    return 'audio/wav';
  };

  const startRecording = async (index: number) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getBestSupportedMimeType();
      const recorder = new MediaRecorder(stream, { mimeType });

      audioChunksRef.current = [];
      mediaRecorderRef.current = recorder;

      // Audio visualizer logic
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      source.connect(analyser);
      audioContextRef.current = audioContext;

      const updateLevel = () => {
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length;
        setAudioLevel(avg);
        setVoiceDetected(avg > 20);
        rafRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        setIsProcessingDictation(true);
        try {
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
          const formData = new FormData();
          formData.append('audio', audioBlob);

          const response = await axios.post(`${API_BASE}/dictation`, formData, {
            headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
          });

          const dictatedText = response.data.text;
          if (dictatedText) {
            updatePosition(index, (p) => {
              const currentDesc = p.description ?? '';
              const newDesc = currentDesc.trim() ? `${currentDesc}\n\n${dictatedText}` : dictatedText;
              return { ...p, description: newDesc };
            });
          }
        } catch (err) {
          console.error('[Dictation] Error:', err);
          enqueueSnackbar('Spracherkennung fehlgeschlagen.', { variant: 'solid', state: 'danger' } as any);
        } finally {
          setIsProcessingDictation(false);
          setRecordingIndex(null);
        }
      };

      recorder.start();
      setRecordingIndex(index);
    } catch (err) {
      console.error('[Dictation] Access denied:', err);
      enqueueSnackbar('Mikrofonzugriff verweigert.', { variant: 'solid', state: 'danger' } as any);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (audioContextRef.current) audioContextRef.current.close();
    setAudioLevel(0);
    setVoiceDetected(false);
  };

  /* ================================================== Helpers ================================================== */
  const movePosition = (from: number, to: number) => {
    if (!isDraft || to < 0 || to >= positions.length) return;
    const next = [...positions];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next.map((p, index) => ({ ...p, sort_order: index })));
  };

  const updatePosition = (
    index: number,
    updater: (pos: PositionData & { _lastChanged?: any }) => PositionData & { _lastChanged?: any }
  ) => {
    const copy = [...positions];
    const updatedRaw = updater(copy[index] as any);
    const updated = isDraft ? recalcPosition(updatedRaw as any) : updatedRaw;
    copy[index] = updated;
    onChange(copy);
  };

  const addPosition = () => onChange([...positions, { p_id: crypto.randomUUID(), pricing: {}, measurement: {} } as PositionData]);
  const removePosition = (index: number) => onChange(positions.filter((_, i) => i !== index));

  const renderInput = (
    value: any,
    label: string,
    type: 'text' | 'number',
    onChangeValue: (value: any) => void,
    options?: { narrow?: boolean; suffix?: string; warn?: boolean }
  ) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-gray-600">{label}</label>
      <div className="relative">
        <input
          type={type}
          className={`input input-sm w-full ${options?.narrow ? 'max-w-[6rem]' : ''} ${options?.suffix ? 'pr-8' : ''} ${options?.warn ? 'border border-red-500' : ''}`}
          value={value ?? ''}
          onChange={(e) => onChangeValue(type === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)}
        />
        {options?.suffix && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">{options.suffix}</span>
        )}
      </div>
    </div>
  );

  const handleFileDownload = async (url: string, fileName: string) => {
    try {
      const fullUrl = `${API_BASE}${url}`;
      const token = localStorage.getItem('authToken');
      const response = await axios.get(fullUrl, {
        responseType: 'blob',
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
        withCredentials: true
      });
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(new Blob([response.data]));
      downloadLink.download = fileName;
      downloadLink.click();
    } catch (error) { console.error(error); }
  };

  const loadSourcePreview = async (sourceId: number) => {
    if (sourcePreviews[sourceId] || loadingSources[sourceId]) return;
    setLoadingSources(p => ({ ...p, [sourceId]: true }));
    try {
      const res = await axios.get<SourcePreview>(`${API_BASE}/position/${sourceId}/source-preview`, { withCredentials: true });
      setSourcePreviews(p => ({ ...p, [sourceId]: res.data }));
    } catch (err) {
      setSourcePreviews(p => ({ ...p, [sourceId]: null }));
    } finally {
      setLoadingSources(p => ({ ...p, [sourceId]: false }));
    }
  };

  /* ================================================== Render ================================================== */
  return (
    <div className="card">
      <div className="card-header flex justify-between items-center">
        <h3 className="card-title">Positionen</h3>
        <button className="btn btn-sm btn-primary" onClick={addPosition} disabled={isProcessingDictation}>
          <KeenIcon icon="plus" /> Position hinzufügen
        </button>
      </div>

      <div className="card-body space-y-6">
        {positions.map((pos, index) => {
          const isRecordingThis = recordingIndex === index;
          const isProcessingThis = isProcessingDictation && recordingIndex === index;
          const warnQuantity = isDraft && needsQuantityWarning(pos);
          const sourceId = pos.source_info?.source_position_id;

          return (
            <div key={pos.p_id || index} className="border rounded p-5 space-y-5 bg-gray-50">
              {/* Header */}
              <div className="flex justify-between items-start gap-4">
                <div className="grid grid-cols-1 md:grid-cols-[6rem_1fr] gap-4 flex-1">
                  {renderInput(pos.position_no, 'Pos.-Nr.', 'text', (v) => updatePosition(index, (p) => ({ ...p, position_no: v })), { narrow: true })}
                  {renderInput(pos.title, 'Titel', 'text', (v) => updatePosition(index, (p) => ({ ...p, title: v })))}
                </div>
                {isDraft && (
                  <div className="flex gap-1 pt-6">
                    <button type="button" className="btn btn-xs btn-light" disabled={index === 0 || isProcessingDictation} onClick={() => movePosition(index, index - 1)}>
                      <KeenIcon icon="arrow-up" />
                    </button>
                    <button type="button" className="btn btn-xs btn-light" disabled={index === positions.length - 1 || isProcessingDictation} onClick={() => movePosition(index, index + 1)}>
                      <KeenIcon icon="arrow-down" />
                    </button>
                  </div>
                )}
              </div>

              {/* Description & Dictation */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-xs text-gray-600 font-medium">Beschreibung</label>

                  <div className="flex items-center gap-3">
                    {isRecordingThis && (
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-gray-200 rounded overflow-hidden">
                          <div
                            className={`h-full transition-all duration-75 ${voiceDetected ? 'bg-primary' : 'bg-gray-400'}`}
                            style={{ width: `${Math.min(audioLevel * 3, 100)}%` }}
                          />
                        </div>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => (isRecordingThis ? stopRecording() : startRecording(index))}
                      disabled={isProcessingDictation && !isRecordingThis}
                      className={`btn btn-xs flex gap-1 items-center ${isRecordingThis ? 'btn-danger' : 'btn-primary'}`}
                    >
                      <KeenIcon icon={isProcessingThis ? 'dots-horizontal' : 'message-text'} />
                      {isRecordingThis ? ' Aufnahme stoppen' : isProcessingThis ? ' Arbeite...' : ' Text einsprechen'}
                    </button>
                  </div>
                </div>

                <textarea
                  className="textarea textarea-sm w-full min-h-[120px] bg-white"
                  value={pos.description ?? ''}
                  disabled={isProcessingThis}
                  onChange={(e) => updatePosition(index, (p) => ({ ...p, description: e.target.value }))}
                  placeholder={isProcessingThis ? "KI verarbeitet Audio..." : "Geben Sie hier den Text ein oder nutzen Sie die Sprachaufnahme."}
                />
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {renderInput(pos.pricing?.unit_price, 'Einzelpreis (netto)', 'number', (v) => updatePosition(index, (p: any) => ({ ...p, pricing: { ...(p.pricing ?? {}), unit_price: v }, ...(isDraft ? { _lastChanged: 'unit_price' } : {}) })), { suffix: '€' })}
                {renderInput(pos.measurement?.quantity, 'Menge', 'number', (v) => updatePosition(index, (p: any) => ({ ...p, measurement: { ...(p.measurement ?? {}), quantity: v }, ...(isDraft ? { _lastChanged: 'quantity' } : {}) })), { warn: warnQuantity })}
                {renderInput(pos.pricing?.total_price, 'Gesamtpreis (netto)', 'number', (v) => updatePosition(index, (p: any) => ({ ...p, pricing: { ...(p.pricing ?? {}), total_price: v }, ...(isDraft ? { _lastChanged: 'total_price' } : {}) })), { suffix: '€' })}
              </div>

              {/* Traceability Details */}
              {(pos.vector_info || pos.source_info || pos.decision_explanation) && (
                <details className="rounded border bg-white p-3" onToggle={(e) => { if (e.currentTarget.open && sourceId) loadSourcePreview(sourceId); }}>
                  <summary className="cursor-pointer text-xs font-medium text-gray-700">Herkunft & Details</summary>
                  <div className="mt-3 text-xs space-y-2">
                    {pos.decision_explanation?.reason_selected && <p><strong>Grund:</strong> {pos.decision_explanation.reason_selected}</p>}
                  </div>
                </details>
              )}

              <div className="flex justify-end">
                <button className="btn btn-sm btn-light text-danger" onClick={() => removePosition(index)} disabled={isProcessingDictation}>
                  <KeenIcon icon="trash" /> Position löschen
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
